import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Container from '../components/Container';
import SectionTitle from '../components/SectionTitle';

/**
 * FAQ page component
 * @returns {React.ReactElement} FAQ page
 */
const FAQPage = () => {
  const [openItem, setOpenItem] = useState(null);

  const toggleItem = (index) => {
    setOpenItem(openItem === index ? null : index);
  };

  const faqItems = [
    {
      question: "What areas do you serve?",
      answer: "We provide drone services throughout Texas, including Austin, Houston, Dallas, San Antonio, and all surrounding areas. For locations outside these regions, please contact us for availability."
    },
    {
      question: "How quickly can you schedule a drone flight?",
      answer: "In most cases, we can schedule drone flights within 3–5 business days. For urgent requests, we offer expedited scheduling when available. Weather conditions may affect scheduling."
    },
    {
      question: "What happens if there's bad weather on the scheduled day?",
      answer: "Safety is our priority. If weather conditions are unfavorable (rain, high winds, etc.), we'll reschedule your flight at no additional cost. We monitor weather forecasts closely and will communicate with you in advance if rescheduling is necessary."
    },
    {
      question: "How do you deliver the final images and videos?",
      answer: "Every project is delivered through our secure LSAS Client Portal, built on Cloudflare’s Zero Trust and enterprise-grade infrastructure. Each client receives protected access to a personalized dashboard, where they can securely view, preview, and download their aerial photos, videos, and mapping deliverables. All connections are encrypted end-to-end, and access is managed through Cloudflare’s authentication layer—ensuring only authorized users can reach their project files. The portal provides a professional, branded experience that reflects the Lone Star Aerial Solutions commitment to quality and confidentiality. For mapping and inspection projects, we also offer data in multiple formats compatible with leading industry software, with rush delivery available upon request."
    },
    {
      question: "Are you FAA certified and insured?",
      answer: "Yes. All flights are conducted by FAA Part 107–certified pilots, and we carry full commercial drone liability insurance for every project."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, ACH transfers, and business checks. For repeat clients and agencies, we also offer invoicing and monthly billing options."
    },
    {
      question: "Can you include ground-level or interior shots?",
      answer: "Absolutely. In addition to aerial photography and videography, we can capture stabilized ground-level or interior footage—perfect for real estate walk-throughs and promotional videos."
    },
    {
      question: "Do you travel outside Texas?",
      answer: "Yes, on a case-by-case basis. Travel fees may apply for projects outside our normal service area. Please contact us to discuss your specific location and project details."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-brand-black text-white">
      <Header />
      
      {/* Page Header */}
      <section className="pt-40 md:pt-48 lg:pt-56 pb-12 bg-brand-black text-white">
        <Container>
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Find answers to common questions about our drone services"
            className="mb-12"
          />
          
          <div className="max-w-3xl mx-auto">
            {faqItems.map((item, index) => (
              <div 
                key={index} 
                className="mb-6 border-b border-brand-blue-600/30 pb-6 last:border-b-0"
              >
                <button
                  className="flex justify-between items-center w-full text-left focus:outline-none"
                  onClick={() => toggleItem(index)}
                >
                  <h3 className="text-xl font-bold text-white">{item.question}</h3>
                  <svg 
                    className={`w-6 h-6 text-brand-blue-600 transition-transform ${openItem === index ? 'transform rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div 
                  className={`mt-4 text-gray-300 transition-all duration-300 overflow-hidden ${openItem === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p>{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
